/**
 * Driver tree — where the fleet's A$ exposure sits, and which machine to
 * target first.
 *
 * ## The tree
 *
 * ```
 * root   total A$ exposure
 * └ mine    Σ its machines
 *   └ machine   its exposure
 *     └ driver    condition · drift · operator behaviour · late PMs · parts lead time
 * ```
 *
 * **A machine's exposure** is one of two things:
 *
 * - **Open alert** — the alert's cost of inaction, `coi(alert).delta`
 *   (unplanned − planned), straight from the engine the Alerts page prices
 *   with.
 * - **Flagged by risk** (no open alert, `predict_risk` score above the 0.35
 *   watch line) — risk × the unplanned cost of a typical breakdown on that
 *   class: mean breakdown downtime × the machine's lost-production rate
 *   (`lostRate`), plus mean breakdown parts cost, plus mean breakdown labour ×
 *   1.6 × the site labour rate (`RATES`) — the same shape and uplift as `coi`.
 *
 * **Driver weights** split a machine's exposure. The `predict_risk` factor
 * points supply four of them — condition (worst component, hours since
 * change and breakdown history), drift, late PMs, abuse → operator. The
 * root-cause finding for the alert component (or the worst component) adds
 * `0.3 × operatorSignal` to operator when it calls `operator` or `mixed`. A
 * part that is out of stock at the site store adds `min(0.3, lead days / 40)`
 * as parts. Weights normalise to shares, so a machine's drivers sum to 1, a
 * mine's machines sum to 1, and the mines sum to 1 at the root.
 *
 * ## Priorities
 *
 * {@link priorities} ranks machines by exposure — "target first" — with each
 * machine's largest driver and the first person its routing decision names.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness.
 */

import { BY_ID, RATES } from '../../data/core.ts';
import type { MachineClass, MineCode, ModelCode } from '../../schema/machine.ts';
import type {
  DriverKind,
  DriverNode,
  RootCause,
  RouteTarget,
  RoutingDecision,
  SiteRole,
} from '../../schema/operations.ts';
import { coi, lostRate } from '../oem-engine.ts';
import { ALERTS } from '../seed/alerts.ts';
import { attribute } from './attribution.ts';
import { round } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';
import { riskFor, RISK_THRESHOLDS, type RiskCtx, type RiskResult } from './risk.ts';
import { rootCause } from './root-cause.ts';
import { partFor, routeAlert, routeFinding, type RoutingCtx } from './routing.ts';

/** What the tree reads: the risk inputs plus the routing inputs. */
export type DriverTreeCtx = RiskCtx & RoutingCtx;

/** Human labels for the five driver kinds. */
export const DRIVER_LABEL: Readonly<Record<DriverKind, string>> = {
  condition: 'Component condition',
  drift: 'Telemetry drift',
  operator: 'Operator behaviour',
  'late-pm': 'Late PMs',
  parts: 'Parts lead time',
};

/** One weighted driver on a machine. */
export interface MachineDriver {
  kind: DriverKind;
  /** Normalised share of the machine's exposure, 0–1. */
  share: number;
  valueAud: number;
  /** Why, in one line. */
  text: string;
}

/** One machine's exposure and what drives it. */
export interface MachineExposure {
  machine: string;
  mine: MineCode;
  model: ModelCode;
  cls: MachineClass;
  valueAud: number;
  basis: 'alert' | 'risk';
  alertId?: string;
  /** The component the exposure is about — the alert's, or the worst one. */
  component: string;
  risk: number;
  drivers: MachineDriver[];
  decision: RoutingDecision;
}

/** Mean breakdown downtime, parts and labour for a class, from work-order history. */
export function breakdownProfile(cls: MachineClass, ctx: Pick<RiskCtx, 'workOrders'>): {
  downtimeH: number;
  partsAud: number;
  labourH: number;
  n: number;
} {
  const rows = ctx.workOrders.filter((w) => w.wo_type === 'Breakdown' && BY_ID[w.machine_id]?.cls === cls);
  const n = rows.length;
  const mean = (f: (w: (typeof rows)[number]) => number) => (n ? rows.reduce((s, w) => s + f(w), 0) / n : 0);
  return {
    // 13 work orders carry a null downtime; they count as zero hours down.
    downtimeH: mean((w) => w.downtime_h ?? 0),
    partsAud: mean((w) => w.parts_cost_aud),
    labourH: mean((w) => w.labour_h),
    n,
  };
}

/** Unplanned cost of a typical breakdown on this machine, priced like `coi`. */
export function unplannedCost(machine: string, ctx: Pick<RiskCtx, 'workOrders'>): number {
  const m = BY_ID[machine];
  const p = breakdownProfile(m.cls, ctx);
  return p.downtimeH * lostRate(machine) + p.partsAud + p.labourH * 1.6 * RATES[m.mine].labour;
}

function driversFor(r: RiskResult, operatorSignal: number, cause: RootCause, partLead: number): { kind: DriverKind; w: number; text: string }[] {
  const w: Record<DriverKind, { w: number; text: string[] }> = {
    condition: { w: 0, text: [] },
    drift: { w: 0, text: [] },
    operator: { w: 0, text: [] },
    'late-pm': { w: 0, text: [] },
    parts: { w: 0, text: [] },
  };
  for (const f of r.factorDetail) {
    const kind: DriverKind =
      f.kind === 'drift' ? 'drift' : f.kind === 'late-pm' ? 'late-pm' : f.kind === 'abuse' ? 'operator' : 'condition';
    w[kind].w += f.points;
    w[kind].text.push(f.text);
  }
  if (cause === 'operator' || cause === 'mixed') {
    w.operator.w += 0.3 * operatorSignal;
    w.operator.text.push(`root cause ${cause} — operator signal ${operatorSignal.toFixed(2)}`);
  }
  if (partLead > 0) {
    w.parts.w += Math.min(0.3, partLead / 40);
    w.parts.text.push(`part not on the site shelf — ${partLead} day lead time`);
  }
  const out = (Object.keys(w) as DriverKind[])
    .filter((k) => w[k].w > 0)
    .map((k) => ({ kind: k, w: w[k].w, text: w[k].text.join('; ') }));
  return out.length ? out : [{ kind: 'condition', w: 1, text: 'condition — no other factor scored' }];
}

/** Every machine with exposure: the open alerts plus the risk-flagged, largest first. */
export function exposureByMachine(ctx: DriverTreeCtx): MachineExposure[] {
  const out: MachineExposure[] = [];
  const alertMachines = new Set(ALERTS.map((a) => a.machine));

  const build = (machine: string, valueAud: number, component: string, basis: 'alert' | 'risk', decision: RoutingDecision, alertId?: string) => {
    const m = BY_ID[machine];
    const r = riskFor(machine, ctx);
    const finding = rootCause(machine, component, ctx);
    const part = partFor(machine, component, ctx);
    const partLead = part && part.siteStock === 0 ? (part.leadTimeDays ?? 0) : 0;
    const ds = driversFor(r, finding.operatorSignal, finding.cause, partLead);
    const W = ds.reduce((s, d) => s + d.w, 0);
    out.push({
      machine,
      mine: m.mine,
      model: m.model,
      cls: m.cls,
      valueAud,
      basis,
      ...(alertId ? { alertId } : {}),
      component,
      risk: r.risk,
      drivers: ds
        .map((d) => ({ kind: d.kind, share: d.w / W, valueAud: (valueAud * d.w) / W, text: d.text }))
        .sort((a, b) => b.share - a.share),
      decision,
    });
  };

  for (const a of ALERTS) {
    const c = coi(a);
    if (!c) continue;
    build(a.machine, c.delta, a.comp, 'alert', routeAlert(a, ctx), a.id);
  }
  for (const m of Object.keys(BY_ID)) {
    if (alertMachines.has(m)) continue;
    const r = riskFor(m, ctx);
    if (r.rawRisk <= RISK_THRESHOLDS.watch) continue;
    const component = r.worstComponent ?? 'Machine - general';
    build(m, r.risk * unplannedCost(m, ctx), component, 'risk', routeFinding(rootCause(m, component, ctx), ctx));
  }
  return out.sort((a, b) => b.valueAud - a.valueAud || a.machine.localeCompare(b.machine));
}

/**
 * The tree. Children's `share`s sum to 1 at every level (to floating-point
 * precision); `valueAud` sums exactly by construction.
 */
export function driverTree(ctx: DriverTreeCtx): DriverNode {
  const exposures = exposureByMachine(ctx);
  const total = exposures.reduce((s, e) => s + e.valueAud, 0);
  const mines = [...new Set(exposures.map((e) => e.mine))];
  const mineNodes: DriverNode[] = mines
    .map((mine) => {
      const es = exposures.filter((e) => e.mine === mine);
      const mv = es.reduce((s, e) => s + e.valueAud, 0);
      const machines: DriverNode[] = es.map((e) => ({
        id: `machine:${e.machine}`,
        label: `${e.machine} · ${e.component}`,
        kind: 'machine',
        valueAud: e.valueAud,
        share: mv ? e.valueAud / mv : 0,
        machine: e.machine,
        mine,
        children: e.drivers.map((d) => ({
          id: `driver:${e.machine}:${d.kind}`,
          label: DRIVER_LABEL[d.kind],
          kind: 'driver',
          valueAud: d.valueAud,
          share: d.share,
          machine: e.machine,
          mine,
          driverKind: d.kind,
          children: [],
        })),
      }));
      return {
        id: `mine:${mine}`,
        label: mine,
        kind: 'mine' as const,
        valueAud: mv,
        share: total ? mv / total : 0,
        mine,
        children: machines,
      };
    })
    .sort((a, b) => b.valueAud - a.valueAud);
  return {
    id: 'root',
    label: 'Total A$ exposure',
    kind: 'root',
    valueAud: total,
    share: 1,
    children: mineNodes,
  };
}

/** One "target first" row. */
export interface Priority {
  rank: number;
  machine: string;
  mine: MineCode;
  model: ModelCode;
  component: string;
  valueAud: number;
  basis: 'alert' | 'risk';
  alertId?: string;
  risk: number;
  topDriver: DriverKind;
  topDriverLabel: string;
  topDriverText: string;
  cause: RootCause;
  confidence: number;
  /** The routed person whose role owns the top driver. */
  owner: RouteTarget | null;
  decision: RoutingDecision;
}

/** The role that owns a driver: behaviour to operations, the rest to maintenance. */
export const DRIVER_OWNER: Readonly<Record<DriverKind, SiteRole>> = {
  operator: 'Operating Supervisor',
  condition: 'Reliability Engineer',
  drift: 'Reliability Engineer',
  'late-pm': 'Maintenance Planner',
  parts: 'Maintenance Planner',
};

/**
 * Machines to target first, by exposure. `owner` is the routed person whose
 * role owns the machine's largest driver (see {@link DRIVER_OWNER}), falling
 * back to the decision's first route.
 */
export function priorities(ctx: DriverTreeCtx, limit = 10): Priority[] {
  const ownerOf = (e: MachineExposure): RouteTarget | null =>
    e.decision.routes.find((r) => r.role === DRIVER_OWNER[e.drivers[0].kind]) ?? e.decision.routes[0] ?? null;
  return exposureByMachine(ctx)
    .slice(0, limit)
    .map((e, i) => ({
      rank: i + 1,
      machine: e.machine,
      mine: e.mine,
      model: e.model,
      component: e.component,
      valueAud: round(e.valueAud, 0),
      basis: e.basis,
      ...(e.alertId ? { alertId: e.alertId } : {}),
      risk: e.risk,
      topDriver: e.drivers[0].kind,
      topDriverLabel: DRIVER_LABEL[e.drivers[0].kind],
      topDriverText: e.drivers[0].text,
      cause: e.decision.cause,
      confidence: e.decision.confidence,
      owner: ownerOf(e),
      decision: e.decision,
    }));
}

/** Fetch everything and build the tree. */
export async function loadDriverTree(): Promise<DriverNode> {
  const inputs = await loadReliabilityInputs();
  return driverTree({ ...inputs, attribution: attribute(inputs) });
}

/** Fetch everything and rank. */
export async function loadPriorities(limit = 10): Promise<Priority[]> {
  const inputs = await loadReliabilityInputs();
  return priorities({ ...inputs, attribution: attribute(inputs) }, limit);
}
